import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Bell, BellRinging, CheckCircle, Trash, Play, Gear, ArrowRight, SealCheck, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function AlertsPanel() {
  const {
    loading, fetchAlerts, markAlertsRead, deleteAlert,
    getRadarConfig, saveRadarConfig, runRadarNow,
    fetchAlertCount,
  } = useApp();

  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [radarConfig, setRadarConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [localConfig, setLocalConfig] = useState({
    enabled: false,
    interval_hours: 6,
    min_score: 75,
    location: "India",
    country: "India",
    hours_old: 24,
    queries: [],
  });
  const [queryInput, setQueryInput] = useState("");

  const loadAlerts = useCallback(async () => {
    const data = await fetchAlerts();
    setAlerts(data.alerts || []);
    setUnreadCount(data.unread_count || 0);
  }, [fetchAlerts]);

  useEffect(() => {
    loadAlerts();
    getRadarConfig().then(cfg => {
      if (cfg) {
        setRadarConfig(cfg);
        setLocalConfig(cfg);
      }
    });
  }, [loadAlerts, getRadarConfig]);

  const handleMarkAllRead = async () => {
    try {
      const result = await markAlertsRead([]);
      if (result) {
        toast.success("All alerts marked as read");
      }
      await loadAlerts();
      await fetchAlertCount();
    } catch (e) {
      toast.error("Failed to mark alerts as read");
    }
  };

  const handleDelete = async (id) => {
    await deleteAlert(id);
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  const handleRunNow = async () => {
    try {
      const r = await runRadarNow();
      toast.success(r.message || "Radar cycle complete");
      await loadAlerts();
      await fetchAlertCount();
    } catch (e) {
      toast.error("Radar run failed");
    }
  };

  const handleSaveConfig = async () => {
    const updated = await saveRadarConfig(localConfig);
    if (updated) {
      setRadarConfig(updated);
      toast.success(updated.enabled ? "Job Radar activated" : "Job Radar paused");
      setShowConfig(false);
    }
  };

  const addQuery = () => {
    if (!queryInput.trim()) return;
    setLocalConfig(prev => ({
      ...prev,
      queries: [...prev.queries, queryInput.trim()]
    }));
    setQueryInput("");
  };

  const removeQuery = (i) => {
    setLocalConfig(prev => ({
      ...prev,
      queries: prev.queries.filter((_, idx) => idx !== i)
    }));
  };

  const scoreColor = (score) => {
    if (score >= 80) return "#10B981";
    if (score >= 60) return "#F59E0B";
    return "#EF4444";
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1100px]" data-testid="alerts-panel">
      {/* Header */}
      <div className="flex items-start justify-between mb-8 animate-in">
        <div>
          <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-white flex items-center gap-3">
            <BellRinging size={32} weight="duotone" className="text-amber-500" />
            Job Radar
          </h1>
          <p className="text-sm text-zinc-500 mt-2">
            Auto-discovers new jobs on schedule, scores them against your resume, and alerts you when a match scores 75%+.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="radar-config-btn"
            onClick={() => setShowConfig(!showConfig)}
            className="bg-transparent border border-white/15 text-zinc-400 hover:bg-white/5 rounded-md h-9 px-4 text-sm"
          >
            <Gear size={16} className="mr-2" /> Configure
          </Button>
          <Button
            data-testid="radar-run-now-btn"
            onClick={handleRunNow}
            disabled={loading.radar}
            className="bg-amber-500 text-black hover:bg-amber-400 rounded-md h-9 px-5 text-sm font-medium"
          >
            {loading.radar ? "Running..." : <><Play size={16} className="mr-2" weight="fill" /> Run Now</>}
          </Button>
        </div>
      </div>

      {/* Radar Status Banner */}
      <div className={`mb-6 p-4 rounded-lg border animate-in delay-1 flex items-center justify-between ${radarConfig?.enabled ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/8 bg-white/[0.02]'}`}
        data-testid="radar-status-banner">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${radarConfig?.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-600'}`} />
          <span className="text-sm text-white font-medium">
            {radarConfig?.enabled ? `Radar Active — runs every ${radarConfig.interval_hours}h` : "Radar Inactive"}
          </span>
          {radarConfig?.enabled && (
            <span className="jp-badge jp-badge-emerald">min score: {radarConfig.min_score}%</span>
          )}
        </div>
        <button
          data-testid="radar-toggle-btn"
          onClick={async () => {
            const newConfig = { ...localConfig, enabled: !localConfig.enabled };
            setLocalConfig(newConfig);
            const updated = await saveRadarConfig(newConfig);
            if (updated) {
              setRadarConfig(updated);
              toast.success(updated.enabled ? "Job Radar activated" : "Job Radar paused");
            }
          }}
          className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${radarConfig?.enabled ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'}`}>
          {radarConfig?.enabled ? "Pause" : "Activate"}
        </button>
      </div>

      {/* Config Panel */}
      {showConfig && (
        <div className="jp-card mb-6 animate-in" data-testid="radar-config-panel">
          <div className="jp-card-header">
            <span className="jp-label">RADAR CONFIGURATION</span>
            <button onClick={() => setShowConfig(false)} className="text-zinc-600 hover:text-white text-lg leading-none">&times;</button>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Interval */}
            <div>
              <div className="jp-label mb-2">CHECK INTERVAL</div>
              <select value={localConfig.interval_hours}
                onChange={e => setLocalConfig(p => ({ ...p, interval_hours: parseInt(e.target.value) }))}
                className="w-full bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white outline-none focus:border-amber-500/40"
                data-testid="radar-interval-select">
                <option value={1}>Every 1 hour</option>
                <option value={3}>Every 3 hours</option>
                <option value={6}>Every 6 hours</option>
                <option value={12}>Every 12 hours</option>
                <option value={24}>Daily</option>
              </select>
            </div>

            {/* Min Score */}
            <div>
              <div className="jp-label mb-2">ALERT THRESHOLD: {localConfig.min_score}%</div>
              <input type="range" min={50} max={95} step={5}
                value={localConfig.min_score}
                onChange={e => setLocalConfig(p => ({ ...p, min_score: parseInt(e.target.value) }))}
                className="w-full accent-amber-500"
                data-testid="radar-threshold-slider" />
              <div className="flex justify-between text-[10px] text-zinc-600 mt-1">
                <span>50% (more alerts)</span><span>95% (fewer, higher quality)</span>
              </div>
            </div>

            {/* Location */}
            <div>
              <div className="jp-label mb-2">LOCATION</div>
              <input value={localConfig.location}
                onChange={e => setLocalConfig(p => ({ ...p, location: e.target.value }))}
                placeholder="e.g. India, Bangalore, Remote"
                className="w-full bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40"
                data-testid="radar-location-input" />
            </div>

            {/* Hours old */}
            <div>
              <div className="jp-label mb-2">JOB FRESHNESS</div>
              <select value={localConfig.hours_old}
                onChange={e => setLocalConfig(p => ({ ...p, hours_old: parseInt(e.target.value) }))}
                className="w-full bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white outline-none focus:border-amber-500/40">
                <option value={12}>Last 12 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={48}>Last 48 hours</option>
                <option value={72}>Last 3 days</option>
              </select>
            </div>

            {/* Custom search queries */}
            <div className="md:col-span-2">
              <div className="jp-label mb-2">MONITORED SEARCH QUERIES</div>
              <p className="text-xs text-zinc-600 mb-2">Leave empty to auto-use your last 3 searches.</p>
              <div className="flex gap-2 mb-2">
                <input value={queryInput} onChange={e => setQueryInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addQuery()}
                  placeholder="e.g. Senior React Developer"
                  className="flex-1 bg-black/40 border border-white/10 rounded-md h-9 px-3 text-sm text-white placeholder:text-zinc-700 outline-none focus:border-amber-500/40"
                  data-testid="radar-query-input" />
                <Button onClick={addQuery} size="sm"
                  className="bg-amber-500/20 text-amber-400 border border-amber-500/20 hover:bg-amber-500/30 rounded-md h-9 px-4 text-sm">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {localConfig.queries.map((q, i) => (
                  <span key={i} className="jp-badge jp-badge-sky flex items-center gap-1.5">
                    {q}
                    <button onClick={() => removeQuery(i)} className="text-sky-400 hover:text-red-400">&times;</button>
                  </span>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 flex gap-3 pt-2">
              <Button onClick={handleSaveConfig}
                className="bg-amber-500 text-black hover:bg-amber-400 rounded-md h-9 px-6 text-sm font-medium"
                data-testid="radar-save-config-btn">
                Save Configuration
              </Button>
              <Button onClick={() => setShowConfig(false)}
                className="bg-transparent border border-white/10 text-zinc-400 hover:bg-white/5 rounded-md h-9 px-4 text-sm">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Alerts List */}
      <div className="jp-card animate-in delay-2" data-testid="alerts-list">
        <div className="jp-card-header">
          <div className="flex items-center gap-3">
            <span className="jp-label">ALERTS</span>
            {unreadCount > 0 && (
              <span className="jp-badge jp-badge-amber">{unreadCount} unread</span>
            )}
          </div>
          {alerts.length > 0 && (
            <Button size="sm" variant="ghost" onClick={handleMarkAllRead}
              className="text-zinc-500 hover:text-white text-xs h-7 px-3"
              data-testid="mark-all-read-btn">
              <CheckCircle size={14} className="mr-1.5" /> Mark all read
            </Button>
          )}
        </div>

        {alerts.length === 0 ? (
          <div className="py-16 text-center" data-testid="alerts-empty">
            <Bell size={40} className="mx-auto text-zinc-700 mb-4" weight="duotone" />
            <div className="text-zinc-500 text-sm mb-2">No alerts yet</div>
            <p className="text-zinc-700 text-xs max-w-[300px] mx-auto">
              Activate Job Radar and click "Run Now" to start scanning for high-match jobs.
              Alerts appear here when a job scores above your threshold.
            </p>
          </div>
        ) : (
          <div>
            {alerts.map((alert, i) => (
              <div key={alert.id}
                data-testid={`alert-item-${i}`}
                className={`flex items-center gap-4 px-5 py-4 ${i < alerts.length - 1 ? 'border-b border-white/5' : ''} hover:bg-white/[0.02] transition-colors ${!alert.read ? 'border-l-2 border-l-amber-500' : ''}`}>
                {/* Score badge */}
                <div className="w-12 text-center shrink-0">
                  <span className="font-mono text-lg font-bold" style={{ color: scoreColor(alert.match_score) }}>
                    {alert.match_score}
                  </span>
                  <div className="text-[9px] text-zinc-700 font-mono">SCORE</div>
                </div>

                {/* Job info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {!alert.read && <SealCheck size={14} className="text-amber-500 shrink-0" weight="fill" />}
                    <span className="text-sm text-white font-medium truncate">{alert.job_title}</span>
                  </div>
                  <div className="text-xs text-zinc-500">{alert.company}</div>
                  {alert.search_query && (
                    <div className="text-[10px] text-zinc-700 mt-1">via: "{alert.search_query}"</div>
                  )}
                </div>

                {/* Time */}
                <div className="text-[10px] text-zinc-700 font-mono shrink-0 flex items-center gap-1">
                  <Clock size={10} />
                  {new Date(alert.created_at).toLocaleDateString()}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  {!alert.read && (
                    <button
                      onClick={async () => {
                        await markAlertsRead([alert.id]);
                        setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, read: true } : a));
                        setUnreadCount(prev => Math.max(0, prev - 1));
                      }}
                      className="p-1.5 text-zinc-600 hover:text-emerald-400 transition-colors"
                      title="Mark read">
                      <CheckCircle size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(alert.id)}
                    className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors"
                    title="Delete">
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* How It Works */}
      <div className="jp-card mt-6 animate-in delay-3" data-testid="radar-how-it-works">
        <div className="jp-card-header">
          <span className="jp-label">HOW JOB RADAR WORKS</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4">
          {[
            { num: "01", title: "Schedule", desc: "Runs at your chosen interval — hourly to daily. Fully automatic." },
            { num: "02", title: "Discover", desc: "Re-runs your saved search queries across Indeed, Google & DDGS." },
            { num: "03", title: "Score", desc: "Every new job is AI-scored against your resume. Deduplication prevents re-processing." },
            { num: "04", title: "Alert", desc: "Jobs scoring above your threshold appear here as unread alerts instantly." },
          ].map((step, i) => (
            <div key={step.num} className={`p-5 ${i < 3 ? 'border-r border-white/5' : ''}`}>
              <span className="font-['Outfit'] text-2xl font-light text-amber-500/30">{step.num}</span>
              <div className="text-sm font-medium text-white mt-2 mb-1">{step.title}</div>
              <div className="text-xs text-zinc-600 leading-relaxed">{step.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
